"use client";

import Image from "next/image";

import cn from "classnames";

import { Text } from "@/components/text/text.component";

import { ProductOptionValue } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import type { ProductTileOption } from "./productTile.component";

export const ProductTileColorVariant = ({
  options,
  onSelectVariant = () => {},
  selectedVariant,
}: ProductTileColorVariantProps) => {
  if (!options?.length) return null;
  const variantOptions = options.find((option) => option.variant === "color");
  if (!variantOptions?.optionValues?.length) return null;

  const optionValues = variantOptions.optionValues as OptionValueWithProduct[];
  const firstTitle = optionValues[0]?.title ?? "";
  const selectedValue = optionValues.find(
    (v) => v.product?.id === selectedVariant,
  );
  const selectedTitle = selectedValue?.title ?? firstTitle;
  const variantProductId = selectedValue?.product?.id;

  const renderVariantImage = (value: OptionValueWithProduct) => {
    if (!value?.image?.length) return null;
    const image = value.image[0];
    const url = urlFor(image).width(64).height(64).url();

    return (
      <Image
        src={url}
        alt={value.title ?? ""}
        width={32}
        height={32}
        className={cn("rounded-full border-2 p-[2px]", {
          "border-secondary": selectedVariant === value?.product?.id,
          "border-transparent": selectedVariant !== value?.product?.id,
        })}
      />
    );
  };

  return (
    <div
      className="mt-4 flex flex-col items-start gap-2 md:flex-row md:items-center"
      data-testid="product-tile-variants">
      {variantProductId ? (
        <input name="variantId" type="hidden" value={variantProductId} />
      ) : null}
      <div className="flex gap-2">
        {optionValues.map((value: OptionValueWithProduct, index: number) => (
          <button
            type="button"
            key={value.product?.id ?? value.id ?? value._key ?? index}
            className="cursor-pointer"
            onClick={() =>
              value.product?.id != null && onSelectVariant(value.product.id)
            }
            aria-pressed={selectedVariant === value?.product?.id}
            data-state={
              selectedVariant === value?.product?.id ? "selected" : undefined
            }
            data-testid="product-variant-button">
            <span className="sr-only">{value.title}</span>
            {renderVariantImage(value)}
          </button>
        ))}
      </div>
      <Text variant="body6" component="p">
        Color: {selectedTitle}
      </Text>
    </div>
  );
};

type ProductTileColorVariantProps = {
  options?: ProductTileOption[] | null;
  selectedVariant?: string | null;
  onSelectVariant?: (variantProductId: string) => void;
};

type OptionValueWithProduct = ProductOptionValue & {
  _key?: string;
  product?: { id?: string } | null;
};
